/**
 * queryClient - Singleton TanStack Query client + IndexedDB persister.
 *
 * This is the central server-state cache for the app. It replaces the previous
 * hand-rolled `useEffect`+`useState`+`fetch` pattern that lived in every page and
 * data hook. Benefits:
 *	 - Request de-duplication + shared cache across components
 *	 - Background refetch / staleness control instead of manual refetch flags
 *	 - Declarative invalidation after mutations (no manual re-fetch calls)
 *	 - The cache is PERSISTED to IndexedDB so reads survive reloads and are
 *	   available offline (complements the existing write-queue in syncService).
 *
 * Coexistence with the offline write-queue (services/offlineStorage.ts +
 * services/syncService.ts):
 *	 - Writes made offline still go through the IndexedDB write-queue (unchanged).
 *	 - This client only caches server READS. After a successful syncAll() the app
 *	   invalidates queries (see context/OfflineContext) so freshly-synced data is
 *	   re-fetched and the cache reflects the server.
 */
import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { idbStorage } from './idbPersister';

/**
 * Bump this whenever the cached server-response SHAPE changes (e.g. a service
 * normalizer changes its output). The persist-client discards any restored cache
 * whose buster differs, preventing stale-shape data from a previous deploy from
 * being rehydrated into new code. Keep in sync with meaningful response changes.
 */
export const PERSIST_BUSTER = 'v1';

/** How long a persisted cache entry may be restored before it is dropped (24h). */
const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24;

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Reads are considered fresh for 30s — within that window components
			// share the cache with no network call. Tunable per-query.
			staleTime: 1000 * 30,
			// Keep unused data in cache for 24h so navigating back is instant and
			// the persisted cache has something to restore.
			gcTime: PERSIST_MAX_AGE,
			// One retry on failure; auth/permission errors aren't worth hammering.
			retry: 1,
			// The app is a focused operations tool, not a live dashboard — avoid
			// surprise refetches every time the user tabs back. Pages that need
			// freshness can opt in per-query.
			refetchOnWindowFocus: false,
		},
		mutations: {
			retry: 0,
		},
	},
});

/**
 * Persister wiring for PersistQueryClientProvider (set up in App.tsx). Serializes
 * the whole cache to a single IndexedDB key via idbStorage.
 */
export const persistOptions = {
	persister: createAsyncStoragePersister({
		storage: idbStorage,
		key: 'REACT_QUERY_OFFLINE_CACHE',
	}),
	maxAge: PERSIST_MAX_AGE,
	buster: PERSIST_BUSTER,
};

/**
 * Wipe the query cache — in-memory AND the persisted IndexedDB copy.
 *
 * Called from the auth teardown (logout + session expiry) alongside
 * clearPersistedFilters(). Query keys carry no user identity, so without
 * this wipe the next user to sign in on the same device is briefly shown
 * the previous user's cached reads (dashboards, lists) until each query
 * refetches — a cross-user data leak on shared field devices.
 */
export async function clearQueryCache(): Promise<void> {
	queryClient.clear();
	try {
		await persistOptions.persister.removeClient();
	} catch {
		/* IndexedDB access can fail (private mode, quota) — the in-memory
		 * clear above still guarantees this session shows no stale data. */
	}
}
